import { Injectable } from '@angular/core';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MatDateFormats,
  NativeDateAdapter,
} from '@angular/material/core';

/** Unambiguous input display: "12 Jul 2026" instead of ambiguous "12/7/2026". */
export const CGX_DATE_FORMATS: MatDateFormats = {
  parse: {
    dateInput: null,
    timeInput: null,
  },
  display: {
    dateInput: {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    },
    timeInput: {
      hour: 'numeric',
      minute: 'numeric',
    },
    monthYearLabel: {
      year: 'numeric',
      month: 'short',
    },
    dateA11yLabel: {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    },
    monthYearA11yLabel: {
      year: 'numeric',
      month: 'long',
    },
    timeOptionLabel: {
      hour: 'numeric',
      minute: 'numeric',
    },
  },
};

/** True when the locale formats day before month (e.g. en-GB). */
export function isDayFirstLocale(locale: string): boolean {
  const parts = new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'numeric',
    year: 'numeric',
  }).formatToParts(new Date(Date.UTC(2020, 0, 2)));
  const dayIndex = parts.findIndex((p) => p.type === 'day');
  const monthIndex = parts.findIndex((p) => p.type === 'month');
  return dayIndex >= 0 && monthIndex >= 0 && dayIndex < monthIndex;
}

/** Parse slash/dot numeric dates using day-first or month-first order. */
export function parseLocaleNumericDate(
  value: string,
  locale: string,
): { year: number; monthIndex: number; day: number } | null {
  const match = value.trim().match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2}|\d{4})$/);
  if (!match) {
    return null;
  }
  const first = Number(match[1]);
  const second = Number(match[2]);
  let year = Number(match[3]);
  if (year < 100) {
    year += 2000;
  }
  const dayFirst = isDayFirstLocale(locale);
  const day = dayFirst ? first : second;
  const monthIndex = (dayFirst ? second : first) - 1;
  if (monthIndex < 0 || monthIndex > 11 || day < 1 || day > 31 || year < 1000) {
    return null;
  }
  return { year, monthIndex, day };
}

/**
 * Native adapter with locale-aware slash/dot date parsing.
 * Material's NativeDateAdapter.parse uses Date.parse, which is MDY in Chromium.
 */
@Injectable()
export class LocaleAwareNativeDateAdapter extends NativeDateAdapter {
  override parse(value: unknown): Date | null {
    if (typeof value === 'number') {
      return new Date(value);
    }
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) {
        return null;
      }
      const parts = parseLocaleNumericDate(trimmed, this.locale);
      if (parts) {
        return this.createDate(parts.year, parts.monthIndex, parts.day);
      }
    }
    return value ? new Date(Date.parse(String(value))) : null;
  }
}

export function provideLocaleAwareNativeDateAdapter() {
  return [
    { provide: DateAdapter, useClass: LocaleAwareNativeDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: CGX_DATE_FORMATS },
  ];
}
